import { httpClient } from '@/services/httpClient';
import { withQuery } from '@/services/LineItemReviewService';

// An admin's reviews (insight-core's documentation/services/reviews.md, submissions.md and unrecognised_tables.md):
// submissions, where a tenant proposes its decisions and recipe for a provider for every tenant, and unrecognised
// tables, tables a provider's statements had that no provider-level table decision covers. Admin only (403 otherwise).
// The backend ignores unknown keys in these bodies, so each body is built here with exactly its keys.
// Responses: insight-core app/schemas/review.py (ReviewPageOut), submission.py (SubmissionDetailOut,
// SubmissionDataprofileOut) and unrecognised_table.py (UnrecognisedTableDetailOut).

/** A page of waiting (`pending`) or resolved reviews, newest first. Superseded submissions are in neither. */
const getReviews = async ({ status, page, pageSize }) => {
    const response = await httpClient.get(withQuery('/api/v1/admin/reviews', { status, page, page_size: pageSize }));
    return response.data;
};

/** A submission, waiting, resolved or superseded, with each decision next to what it would replace. */
const getSubmission = async (submissionId) => {
    const response = await httpClient.get(`/api/v1/admin/submissions/${submissionId}`);
    return response.data;
};

/**
 * The distinct tables of the tenant's live statements for the submission's provider and Schema (`file_id` is the
 * attached statement); an empty list when none is profiled.
 */
const getSubmissionProfile = async (submissionId) => {
    const response = await httpClient.get(`/api/v1/admin/submissions/${submissionId}/statement/dataprofile`);
    return response.data;
};

/**
 * Approves from the version read: as a new provider, or merged into an approved one (`mergeIntoProviderId`). A
 * `recipe` ({steps, switches}) replaces the submitted one wholly; send it only when the admin adjusted it. Custom
 * transformations listed in `deselectedCustomTransformationIds` aren't shared with every tenant. Answers the submission
 * as approved.
 */
const approveSubmission = async (submissionId, { version, recipe, mergeIntoProviderId, deselectedCustomTransformationIds }) => {
    const response = await httpClient.post(`/api/v1/admin/submissions/${submissionId}/approve`, {
        version,
        deselected_custom_transformation_ids: deselectedCustomTransformationIds,
        recipe,
        merge_into_provider_id: mergeIntoProviderId,
    });
    return response.data;
};

/** Rejects from the version read; rejecting takes no note. */
const rejectSubmission = async (submissionId, { version }) => {
    const response = await httpClient.post(`/api/v1/admin/submissions/${submissionId}/reject`, { version });
    return response.data;
};

/** An unrecognised table, with its fields' sample values, the known tables with their labels, and a suggestion. */
const getUnrecognisedTable = async (tableId) => {
    const response = await httpClient.get(`/api/v1/admin/unrecognised-tables/${tableId}`);
    return response.data;
};

/** Approves it as a new table: used or skipped, with its field labels. */
const approveUnrecognisedTable = async (tableId, { disposition, labels }) => {
    const response = await httpClient.post(`/api/v1/admin/unrecognised-tables/${tableId}/approve`, {
        disposition,
        labels,
    });
    return response.data;
};

/** Rejects it; rejecting takes no note. */
const rejectUnrecognisedTable = async (tableId) => {
    const response = await httpClient.post(`/api/v1/admin/unrecognised-tables/${tableId}/reject`, {});
    return response.data;
};

export {
    getReviews,
    getSubmission,
    getSubmissionProfile,
    approveSubmission,
    rejectSubmission,
    getUnrecognisedTable,
    approveUnrecognisedTable,
    rejectUnrecognisedTable,
};
